import {
  CAROUSEL_DWELL_DEFAULT_MS,
  CAROUSEL_SCROLL_SPEED_DEFAULT,
} from './carouselLayout';

/**
 * Styles de Carrousel — un style n'est PAS une variante de rendu, c'est un jeu
 * de valeurs. Rien n'est persisté qui dirait « ceci est un bandeau de logos » :
 * choisir un style en recopie les valeurs dans le carrousel, qui reste
 * « Personnalisé » (seul un style du thème se suit).
 *
 * Même mécanique que les styles de Carte info
 * (shared/boxes/infoCard/infoCardStyles.js), qui la tenait des dispositions de
 * section (utils/proposalSectionPresets.js).
 *
 * DÉPENDANCES : ce module ne connaît QUE carouselLayout. C'est
 * carouselDefaults.js qui l'importe pour construire ses défauts, jamais
 * l'inverse — sinon le cycle rendrait la base `undefined` au chargement.
 */

/**
 * Base commune : chaque style ne réécrit que ce qui le distingue. C'est aussi
 * la source des défauts du bloc (carouselDefaultProps), donc un carrousel
 * fraîchement posé correspond au style « Diaporama » par construction.
 */
const base = {
  height: '25rem',
  // 500 px : une fenêtre de ruban vertical, pas une hauteur de slide.
  stripHeight: '31.25rem',
  slideGap: '1rem',
  mediaFit: 'cover',
  showSlideText: true,
  maxSlidesVisible: false,
  slidesVisible: 1,
  scrollAxis: 'horizontal',
  scrollDirection: 'forward',
  loop: true,
  indicators: true,
  thumbnails: false,
  controls: true,
  autoplay: false,
  autoplayDelay: CAROUSEL_DWELL_DEFAULT_MS,
  autoplayPauseOnHover: true,
  scrollSpeed: CAROUSEL_SCROLL_SPEED_DEFAULT,
  edgeFade: true,
  edgeFadeWidth: '3rem',
};

/**
 * Les props qu'un style pose : tout le bloc sauf `slides`. Le contenu d'un
 * carrousel, ce sont ses slides — un changement de style ne doit jamais y
 * toucher, sinon essayer un style efface le travail de l'utilisateur. Tirées
 * de `base` : un réglage ajouté au carrousel y prend sa valeur, et devient
 * du même coup une prop de style.
 */
export const CAROUSEL_STYLE_PROPS = Object.keys(base);

/**
 * Valeurs propres au bloc (cf. `seed`, shared/theme/styleSchema) : un style
 * du thème les pose, mais elles dépendent du carrousel — deux rubans de logos
 * qui défilent en sens opposés ont le même style. Les régler ne détache pas
 * le carrousel de son style.
 */
export const CAROUSEL_SEED_PROPS = [
  'height', 'stripHeight', 'scrollDirection', 'scrollSpeed', 'autoplayDelay', 'showSlideText',
];

const preset = (key, label, props) => ({ key, label, props: { ...base, ...props } });

/**
 * L'ordre est celui de la galerie : d'abord les diaporamas pas-à-pas, puis les
 * rubans continus.
 *
 * `maxSlidesVisible` + `autoplay` est ce couple qui fait passer le bloc
 * d'Autoplay (arrêt sur chaque slide) à AutoScroll (défilement sans fin) —
 * cf. carouselLayout. Aucun style ne coche l'un sans l'autre.
 */
export const CAROUSEL_STYLE_PRESETS = [
  preset('slideshow', 'Diaporama', {}),
  preset('slideshow-auto', 'Diaporama automatique', {
    autoplay: true,
  }),
  preset('cards', 'Galerie 3 colonnes', {
    slidesVisible: 3,
    height: '18rem',
    // Trois slides d'un coup : les points ne désignent plus rien de précis.
    indicators: false,
  }),
  preset('logo-strip', 'Bandeau de logos', {
    maxSlidesVisible: true,
    autoplay: true,
    scrollSpeed: 1,
    controls: false,
    indicators: false,
    // Sans ça, les zones de texte vides — rendues en édition pour rester
    // cliquables — écrasent le logo : dans une slide basse, le média est le
    // seul élément compressible du flex.
    showSlideText: false,
    height: '6rem',
    slideGap: '3rem',
    // Un logo n'est jamais recadré.
    mediaFit: 'contain',
  }),
  preset('vertical-strip', 'Colonne défilante', {
    scrollAxis: 'vertical',
    maxSlidesVisible: true,
    autoplay: true,
    scrollSpeed: 1,
    controls: false,
    indicators: false,
    // Comme tout ruban : la slide n'est que son média. Une zone de texte prend
    // la hauteur de la slide et écrase le média, seul élément compressible du
    // flex.
    showSlideText: false,
    // À la verticale, `height` est la hauteur d'UNE slide ; `stripHeight` est
    // la portion de colonne visible.
    height: '10rem',
  }),
];

export const CAROUSEL_DEFAULT_STYLE_KEY = 'slideshow';

/** Le style de clé donnée — les défauts du bloc s'en servent. */
export function carouselStyleByKey(key) {
  return CAROUSEL_STYLE_PRESETS.find((def) => def.key === key) || null;
}

/** Le patch qu'un style écrit — rien d'autre, et jamais `slides`. */
export function carouselStylePatch(def) {
  return { ...def.props };
}

/**
 * Applique un style : seules les props de style sont écrites, les slides sont
 * conservées telles quelles.
 */
export function applyCarouselStyle(props, def) {
  return { ...(props || {}), ...carouselStylePatch(def) };
}
